'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';

import { cn } from '@/lib/styleUtils';
import { PuzzlePrompt, PuzzleScene } from '@/modules/captcha/components/PuzzleFrame';
import { BAY_LEFTS, randomInt, shuffle } from '@/modules/captcha/shared/puzzle-utils';
import { useTapChallenge } from '@/modules/captcha/hooks/useTapChallenge';
import { shakeAnimation, slotBase, slotCorrect, slotIdle, slotWrong } from './puzzle.style';
import type { PuzzleProps } from './puzzle.types';

const SCENE_HEIGHT = 140;
const LABELS = ['P1', 'P2', 'P3', 'P4'] as const;

/**
 * Design 2b — tap the named parking bay.
 *
 * ## Invariants:
 * - The labels are shuffled across the bays, and the answer is picked from the shuffled order.
 *   Why: leaving P1..P4 in position order means the prompt's number IS the position, so the
 *   challenge reduces to reading one digit — no scanning, and trivially scriptable.
 */
export const FindTheSpot = ({ onSolved }: PuzzleProps) => {
  const t = useTranslations('Captcha');
  const [labels] = useState(() => shuffle(LABELS));
  const [answer] = useState(() => randomInt(LABELS.length));
  const { wrongIndex, solved, pick } = useTapChallenge(answer, onSolved);

  return (
    <>
      <PuzzlePrompt>{t('findTheSpotPrompt', { label: labels[answer] })}</PuzzlePrompt>
      <PuzzleScene height={SCENE_HEIGHT} data-cy="captcha-find-the-spot">
        {BAY_LEFTS.map((left, index) => {
          const isWrong = index === wrongIndex;
          const isCorrect = solved && index === answer;
          return (
            <button
              key={left}
              type="button"
              onClick={() => pick(index)}
              className={cn(
                slotBase,
                'tw:top-small tw:flex tw:h-[116px] tw:w-[66px] tw:cursor-pointer tw:items-center tw:justify-center',
                'tw:focus-visible:outline-2 tw:focus-visible:outline-offset-2 tw:focus-visible:outline-primary',
                !isWrong && !isCorrect && slotIdle,
                isCorrect && slotCorrect,
                isWrong && cn(slotWrong, shakeAnimation)
              )}
              style={{ left }}
              data-cy={`captcha-spot-${index}`}
            >
              <span
                className={cn(
                  'tw:font-mono tw:text-16 tw:font-bold',
                  isCorrect ? 'tw:text-success' : 'tw:text-font'
                )}
              >
                {labels[index]}
              </span>
            </button>
          );
        })}
      </PuzzleScene>
    </>
  );
};

FindTheSpot.displayName = 'FindTheSpot';
